import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '#client/lib/cn';

/** How long the copy button shows its tick. */
const COPIED_FOR_MS = 1500;

interface IdentifierProps {
  value: string;
  /**
   * Trim a known prefix for display while still copying the full value.
   * PINs are stored as `pin_2026-08-31-123456`; the `pin_` adds nothing on
   * screen but must survive a copy so it can be pasted back into a search.
   */
  stripPrefix?: string | undefined;
  /** Adds a click-to-copy affordance. Off inside dense table rows by default. */
  copyable?: boolean | undefined;
  className?: string | undefined;
}

/**
 * A machine identifier — PIN, MR number, accession, barcode.
 *
 * Rendered in the mono face with tabular figures and a slashed zero, because
 * these get read aloud down a phone line and dictated between benches. `0`
 * against `O` and `1` against `l` are the two confusions that actually cause
 * mis-filed samples, and the slashed zero is what removes the first.
 */
export function Identifier({ value, stripPrefix, copyable = false, className }: IdentifierProps) {
  const [isCopied, setIsCopied] = useState(false);

  // The tick reverts on its own; cleared on unmount or a second copy, so no
  // timer outlives the button it belongs to.
  useEffect(() => {
    if (!isCopied) return;
    const timer = setTimeout(() => setIsCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  const display =
    stripPrefix && value.startsWith(stripPrefix) ? value.slice(stripPrefix.length) : value;

  if (!copyable) {
    return <span className={cn('ident', className)}>{display}</span>;
  }

  const handleCopy = () => {
    // Always copy the full value, never the shortened display form.
    void navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
    });
  };

  return (
    <span className={cn('c-identifier', className)}>
      <span className="ident">{display}</span>
      <button
        type="button"
        className="c-identifier__copy"
        onClick={handleCopy}
        aria-label={isCopied ? 'Copied' : `Copy ${value}`}
      >
        {isCopied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </button>
    </span>
  );
}
